/**
 * <bundle-tshirt> Custom Element
 *
 * - Size selection & variant switching
 * - AJAX add-to-cart
 * - Image gallery thumbnails
 * - Pack & color switching via Section Rendering API (no page reload)
 * - Browser back/forward support
 */
class BundleTshirt extends HTMLElement {
  connectedCallback() {
    // These bind to `this` (the element) — only once, ever
    if (!this._globalsBound) {
      this._globalsBound = true;
      this.#bindNavigation();
      window.addEventListener('popstate', () => this.#onPopState());
    }

    this.#init();
  }

  #init() {
    this.product = this.#parseProductJSON();
    if (!this.product) return;

    this.elements = {
      form: this.querySelector('form'),
      variantSelect: this.querySelector('[name="id"]'),
      submitBtn: this.querySelector('.bundle-atc__button'),
      price: this.querySelector('.bundle-price__current'),
      comparePrice: this.querySelector('.bundle-price__compare'),
      thumbs: this.querySelectorAll('.bundle-gallery__thumb'),
      mainImages: this.querySelectorAll('.bundle-gallery__main-image'),
    };

    // These bind to child elements (replaced on each swap) — safe to re-bind
    this.#bindSizes();
    this.#bindGallery();
    this.#bindForm();
    this.#bindReviewsBadge();
    this.#syncFromURL();
  }

  // ══════════════════════════════════════
  //  Product Data
  // ══════════════════════════════════════

  #parseProductJSON() {
    const el = this.querySelector('script[type="application/json"]');
    try {
      const product = JSON.parse(el?.textContent);
      this.#enrichVariantsWithInventory(product);
      return product;
    }
    catch { return null; }
  }

  #enrichVariantsWithInventory(product) {
    if (!product?.variants?.length) return;

    const select = this.querySelector('[name="id"]');
    if (!select) return;

    [...select.options].forEach(opt => {
      const variant = product.variants.find(v => String(v.id) === opt.value);
      if (!variant) return;

      const qty = parseInt(opt.dataset.inventoryQuantity, 10);
      variant.inventory_quantity = Number.isFinite(qty) ? qty : null;
      variant.inventory_management = opt.dataset.inventoryManagement || null;
    });
  }

  #getSelectedOptions() {
    const selected = {};
    this.querySelectorAll('.bundle-size-btn.is-active').forEach(btn => {
      selected[btn.dataset.optionIndex] = btn.dataset.value;
    });
    return selected;
  }

  #findVariantBySelection() {
    const selected = this.#getSelectedOptions();
    if (!Object.keys(selected).length) return null;

    return this.product.variants.find(v => {
      if (selected['1'] !== undefined && v.option1 !== selected['1']) return false;
      if (selected['2'] !== undefined && v.option2 !== selected['2']) return false;
      if (selected['3'] !== undefined && v.option3 !== selected['3']) return false;
      return true;
    });
  }

  #variantOption(variant, optionIndex) {
    if (optionIndex === '1' || optionIndex === 1) return variant.option1;
    if (optionIndex === '2' || optionIndex === 2) return variant.option2;
    if (optionIndex === '3' || optionIndex === 3) return variant.option3;
    return null;
  }

  #currentVariant() {
    const id = parseInt(this.elements.variantSelect?.value, 10);
    return this.product.variants.find(v => v.id === id);
  }

  // ══════════════════════════════════════
  //  Pack & Color Navigation (no reload)
  // ══════════════════════════════════════

  #bindNavigation() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('.bundle-pack-card, .bundle-colors__item');
      if (!link || link.classList.contains('is-active')) return;

      const href = link.getAttribute('href');
      if (!href) return;

      e.preventDefault();
      this.#navigateTo(href);
    });
  }

  async #navigateTo(productUrl) {
    this.classList.add('is-loading');

    // Remember selected size before swap
    const activeSize = this.querySelector('.bundle-size-btn.is-active')?.dataset.value;

    try {
      const separator = productUrl.includes('?') ? '&' : '?';
      const res = await fetch(`${productUrl}${separator}section_id=product-template-bundle-tshirt`);
      if (!res.ok) throw new Error('Failed to fetch section');

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newElement = doc.querySelector('bundle-tshirt');

      if (!newElement) throw new Error('Section not found in response');

      // Swap content
      this.innerHTML = newElement.innerHTML;

      // Copy data attributes
      for (const attr of newElement.attributes) {
        if (attr.name !== 'class') this.setAttribute(attr.name, attr.value);
      }

      // Re-init bindings with new content
      this.#init();
      this.#reinitJudgeMe();

      // Restore size selection (or fall back to first available)
      this.#restoreSize(activeSize);

      // Update URL with variant
      const variant = this.#currentVariant();
      const url = new URL(productUrl, location.origin);
      if (variant) url.searchParams.set('variant', variant.id);
      history.pushState({ bundleNav: true }, '', url.toString());

    } catch {
      window.location.href = productUrl;
    } finally {
      this.classList.remove('is-loading');
    }
  }

  #restoreSize(sizeValue) {
    if (!this.product) return;

    // Try the previously selected size (option1) — find first available variant with it
    if (sizeValue) {
      const variant = this.product.variants.find(v => v.option1 === sizeValue && v.available);
      if (variant) {
        this.#applyVariantToButtons(variant);
        this.#setVariant(variant);
        return;
      }
    }

    // Fall back to first available variant
    const firstAvailable = this.product.variants.find(v => v.available);
    if (firstAvailable) {
      this.#applyVariantToButtons(firstAvailable);
      this.#setVariant(firstAvailable);
    }
  }

  #applyVariantToButtons(variant) {
    this.#activateButtonForOption(1, variant.option1);
    this.#activateButtonForOption(2, variant.option2);
    this.#activateButtonForOption(3, variant.option3);
    this.#updateOptionAvailability();
  }

  #onPopState() {
    // Re-fetch section for the current URL
    this.#navigateReplace(location.pathname + location.search);
  }

  async #navigateReplace(url) {
    this.classList.add('is-loading');
    try {
      const separator = url.includes('?') ? '&' : '?';
      const res = await fetch(`${url}${separator}section_id=product-template-bundle-tshirt`);
      if (!res.ok) throw new Error();

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newElement = doc.querySelector('bundle-tshirt');

      if (newElement) {
        this.innerHTML = newElement.innerHTML;
        for (const attr of newElement.attributes) {
          if (attr.name !== 'class') this.setAttribute(attr.name, attr.value);
        }
        this.#init();
        this.#reinitJudgeMe();
      }
    } catch {
      location.reload();
    } finally {
      this.classList.remove('is-loading');
    }
  }

  // ══════════════════════════════════════
  //  Size Selection
  // ══════════════════════════════════════

  #bindSizes() {
    this.querySelectorAll('.bundle-size-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('bundle-size-btn--disabled')) return;

        const optionIndex = btn.dataset.optionIndex;

        // Deactivate only buttons in the SAME option group (size, inseam, etc.)
        this.querySelectorAll(`.bundle-size-btn[data-option-index="${optionIndex}"]`)
          .forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        // Update label inside the same option container
        this.#updateLabelForButton(btn);

        // Re-evaluate availability of buttons in OTHER option groups based on new selection
        this.#updateOptionAvailability();

        // Combine all selected options to find the matching variant
        const variant = this.#findVariantBySelection();
        if (variant) this.#setVariant(variant);
      });
    });
  }

  #updateLabelForButton(btn) {
    const container = btn.closest('.bundle-sizes');
    const labelEl = container?.querySelector('.bundle-sizes__selected');
    if (labelEl) labelEl.textContent = btn.dataset.value;
  }

  #updateOptionAvailability() {
    const selected = this.#getSelectedOptions();

    this.querySelectorAll('.bundle-size-btn').forEach(btn => {
      const optionIndex = btn.dataset.optionIndex;
      const value = btn.dataset.value;

      // A button stays enabled if there's any AVAILABLE variant where:
      //  - this button's option position == this button's value
      //  - all OTHER currently-selected options also match
      const isAvailable = this.product.variants.some(v => {
        if (!v.available) return false;
        if (this.#variantOption(v, optionIndex) !== value) return false;
        for (const [idx, val] of Object.entries(selected)) {
          if (idx === optionIndex) continue;
          if (this.#variantOption(v, idx) !== val) return false;
        }
        return true;
      });

      btn.classList.toggle('bundle-size-btn--disabled', !isAvailable);
      btn.disabled = !isAvailable;
    });
  }

  #syncFromURL() {
    const variantId = new URLSearchParams(location.search).get('variant');

    if (variantId) {
      const variant = this.product.variants.find(v => String(v.id) === variantId);
      if (variant) {
        this.#applyVariantToButtons(variant);
        this.#setVariant(variant);
        return;
      }
    }

    // No ?variant= in URL — Liquid pre-rendered active buttons via option.selected_value.
    // Update labels for each pre-active button and refresh cross-option availability.
    this.querySelectorAll('.bundle-size-btn.is-active').forEach(btn => this.#updateLabelForButton(btn));
    this.#updateOptionAvailability();
  }

  #activateButtonForOption(optionIndex, value) {
    if (value === undefined || value === null) return;
    this.querySelectorAll(`.bundle-size-btn[data-option-index="${optionIndex}"]`)
      .forEach(b => b.classList.remove('is-active'));
    const safeValue = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(value) : value;
    const target = this.querySelector(`.bundle-size-btn[data-option-index="${optionIndex}"][data-value="${safeValue}"]`);
    if (target) {
      target.classList.add('is-active');
      this.#updateLabelForButton(target);
    }
  }

  // ══════════════════════════════════════
  //  Variant Switching
  // ══════════════════════════════════════

  #setVariant(variant) {
    const { variantSelect, price, comparePrice, submitBtn } = this.elements;
    if (!variantSelect) return;

    // Update <select>
    const id = String(variant.id);
    [...variantSelect.options].forEach(opt => { opt.selected = opt.value === id; });
    variantSelect.value = id;

    // Price
    if (price) price.textContent = this.#money(variant.price);
    if (comparePrice) {
      const hasCompare = variant.compare_at_price > variant.price;
      comparePrice.textContent = hasCompare ? this.#money(variant.compare_at_price) : '';
      comparePrice.style.display = hasCompare ? '' : 'none';
    }

    // Button
    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      submitBtn.innerHTML = variant.available
        ? `Add to Cart &mdash; ${this.#money(variant.price)}`
        : 'Sold Out';
    }

    // URL (replace, not push — size change is not a "page")
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);

    // Image
    if (variant.featured_image) this.#switchImage(String(variant.featured_image.id));

    this.#updateLowStock(variant);
  }

  #updateLowStock(variant) {
    const thresholdMsg = this.querySelector('[data-low-stock-threshold]');
    if (!thresholdMsg) return;

    const managed = Boolean(variant?.inventory_management);
    const qty = variant?.inventory_quantity;
    const show = managed && Number.isFinite(qty) && qty > 0 && qty < 10;

    thresholdMsg.classList.toggle('is-hidden', !show);
    if (show) {
      thresholdMsg.removeAttribute('hidden');
    } else {
      thresholdMsg.setAttribute('hidden', '');
    }
  }

  // ══════════════════════════════════════
  //  Gallery
  // ══════════════════════════════════════

  #bindGallery() {
    this.elements.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.#switchImage(thumb.dataset.imageId));
    });
  }

  #switchImage(imageId) {
    this.elements.thumbs.forEach(t =>
      t.classList.toggle('is-active', t.dataset.imageId === imageId)
    );
    this.elements.mainImages.forEach(img =>
      img.classList.toggle('is-active', img.dataset.imageId === imageId)
    );
  }

  // ══════════════════════════════════════
  //  AJAX Cart
  // ══════════════════════════════════════

  #bindForm() {
    this.elements.form?.addEventListener('submit', async (e) => {
      if (this.dataset.ajax !== 'true') return;
      e.preventDefault();

      const btn = this.elements.submitBtn;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(this.elements.form),
        });

        if (!res.ok) {
          const { description } = await res.json();
          throw new Error(description || 'Could not add to cart');
        }

        this.#onCartSuccess();
      } catch (err) {
        this.#notify('error', err.message);
      } finally {
        this.#restoreButton();
      }
    });
  }

  async #onCartSuccess() {
    this.#notify('success');
    document.querySelector('.site-header__cart')?.classList.add('has-items');
    document.querySelector('.site-header__cart-indicator')?.classList.remove('hide');

    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const countEl = document.querySelector('.site-header__cart-count');
      if (countEl) countEl.textContent = cart.item_count;

      const cartLink = document.querySelector('.site-header__cart');
      if (!cartLink) return;

      if (cart.item_count > 0) {
        cartLink.classList.add('has-items');
        document.querySelector('.site-header__cart-indicator')?.classList.remove('hide');
      } else {
        cartLink.classList.remove('has-items');
        document.querySelector('.site-header__cart-indicator')?.classList.add('hide');
      }
    } catch { /* silent */ }
  }

  #restoreButton() {
    const variant = this.#currentVariant();
    const btn = this.elements.submitBtn;
    if (!btn) return;

    btn.disabled = !variant?.available;
    btn.innerHTML = variant?.available
      ? `Add to Cart &mdash; ${this.#money(variant.price)}`
      : 'Sold Out';
  }

  // ══════════════════════════════════════
  //  Reviews Badge → Scroll to Reviews
  // ══════════════════════════════════════

  #bindReviewsBadge() {
    const badge = this.querySelector('.jdgm-preview-badge');
    const reviews = this.querySelector('#judgeme_product_reviews');
    if (!badge || !reviews) return;

    badge.style.cursor = 'pointer';
    badge.addEventListener('click', (e) => {
      e.preventDefault();
      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ══════════════════════════════════════
  //  Re-init Judge.me after AJAX swap
  // ══════════════════════════════════════

  #reinitJudgeMe() {
    if (typeof jdgm !== 'undefined' && jdgm.customizeBadges) {
      jdgm.customizeBadges();
    }
    if (typeof jdgm !== 'undefined' && jdgm.customizeWidgets) {
      jdgm.customizeWidgets();
    }
  }

  // ══════════════════════════════════════
  //  Helpers
  // ══════════════════════════════════════

  #notify(type, message = false) {
    if (typeof theme !== 'undefined' && theme.Notify) {
      theme.Notify.open(type, message, true);
    } else if (type === 'error' && message) {
      alert(message);
    }
  }

  #money(cents) {
    return theme.Currency.formatMoney(cents, theme.moneyFormat);
  }
}

customElements.define('bundle-tshirt', BundleTshirt);
